import { Form, Input, Modal } from 'antd';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { applicationFormalApprovalSendToAction } from '~Applications/actions';
import ModalStateEnum from '~UI/ModalStateEnum';
import { validateEmail as emailValidator } from '~utilities/validators';

interface IFormalApprovalSendError {
    email?: string;
    firstName?: string;
    lastName?: string;
}

export default function FormalApprovalSendModal(props: {
    formalApprovalUuid: string;
    sendModalState: ModalStateEnum;
    onCancel: () => void;
}): ReactElement {
    const { sendModalState, formalApprovalUuid, onCancel } = props;

    const [ email, setEmail ] = useState<string>(null);
    const [ errors, setErrors ] = useState<IFormalApprovalSendError>({});
    const [ firstName, setFirstName ] = useState<string>(null);
    const [ lastName, setLastName ] = useState<string>(null);

    const dispatch: Dispatch = useDispatch();

    const onChangeFirstName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
    }, []);

    const onChangeLastName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    }, []);

    const onChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    }, []);

    const validateEmail: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!email || email.length === 0) {
            error = 'Please enter the email address';
        } else if (!emailValidator(email)) {
            error = 'Please enter a valid email address';
        }

        setErrors((prevErrors: IFormalApprovalSendError) => ({
            ...prevErrors,
            email: error,
        }));

        return !error;
    }, [
        email,
    ]);

    const validateFirstName: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!firstName || firstName.length === 0) {
            error = 'Please enter the first name';
        }

        setErrors((prevErrors: IFormalApprovalSendError) => ({
            ...prevErrors,
            firstName: error,
        }));

        return !error;
    }, [
        firstName,
    ]);

    const validateLastName: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!lastName || lastName.length === 0) {
            error = 'Please enter the last name';
        }

        setErrors((prevErrors: IFormalApprovalSendError) => ({
            ...prevErrors,
            lastName: error,
        }));

        return !error;
    }, [
        lastName,
    ]);

    const onClickOk: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateEmail() && valid;
        valid = validateFirstName() && valid;
        valid = validateLastName() && valid;

        if (!valid) {
            return;
        }

        dispatch(applicationFormalApprovalSendToAction(formalApprovalUuid, email, firstName, lastName));
        onCancel();
    }, [
        dispatch,
        email,
        firstName,
        formalApprovalUuid,
        lastName,
        onCancel,
        validateEmail,
        validateFirstName,
        validateLastName,
    ]);

    return (
        <Modal
            okText='Send'
            onCancel={onCancel}
            onOk={onClickOk}
            open={sendModalState === ModalStateEnum.Open}
            title='Send Formal Approval'
            wrapClassName='application-formal-approval-send-modal'
        >
            <Form.Item label='First Name' className='first-name' validateStatus={errors.firstName && 'error'} help={errors.firstName}>
                <Input onBlur={validateFirstName} onChange={onChangeFirstName} value={firstName} />
            </Form.Item>
            <Form.Item label='Last Name' className='last-name' validateStatus={errors.lastName && 'error'} help={errors.lastName}>
                <Input onBlur={validateLastName} onChange={onChangeLastName} value={lastName} />
            </Form.Item>
            <Form.Item label='Email' className='email' validateStatus={errors.email && 'error'} help={errors.email}>
                <Input onBlur={validateEmail} onChange={onChangeEmail} value={email} />
            </Form.Item>
        </Modal>
    );
}